import {
  Box,
  Stack,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Tooltip,
  ButtonBase,
  Alert,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSettingsContext } from "../SettingsProvider";
import { SettingsPageHeader, FloatingSaveButton, I18nField } from "../components";
import { useDebounce } from "@/hooks/use-debounce";
import { v4 as uuid } from "uuid";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import * as Icons from "@/assets/icons";
import { css, Theme } from "@/theme";

const styles = {
  taskItem: (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    justify-content: flex-start;
    text-align: start;

    &:focus-visible, &:focus, &:focus-within {
      box-shadow: inset 0 0 0px 2px;
    }
  `,
};

// Debounced state name field component to optimize translation lookup
const StateNameField = ({ 
  value, 
  onChange, 
  error 
}: { 
  value: string; 
  onChange: (value: string) => void; 
  error: boolean;
}) => {
  const { t } = useTranslation();
  const debouncedValue = useDebounce(value, 300);
  
  const translation = useMemo(() => {
    if (!debouncedValue) return '';
    return t(`tasks.${debouncedValue}`, debouncedValue);
  }, [debouncedValue, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Replace spaces with underscores and convert to lowercase
    const normalizedValue = inputValue.replace(/\s+/g, '_').toLowerCase();
    onChange(normalizedValue);
  };

  return (
    <TextField
      label="State Name"
      value={value}
      onChange={handleChange}
      placeholder="Enter state name (e.g., 'not_requested')"
      fullWidth
      variant="outlined"
      error={error}
      helperText={
        error
          ? 'State names cannot contain spaces. Use underscores instead (e.g., "not_requested")'
          : value
            ? `Translation: ${translation}`
            : ''
      }
    />
  );
};

type TaskState = {
  id: string;
  name: string;
  colour: string;
  order: number;
};

type TaskDefinition = {
  id: string;
  name: string;
  icon: string;
  states: TaskState[];
};

// Default states that will be added to new task definitions
// State names are translation keys (no spaces, used with tasks namespace)
const DEFAULT_STATES: Omit<TaskState, "id">[] = [
  { name: "not_requested", colour: "#C3C8DE", order: 0 },
  { name: "requested", colour: "#FCD3B8", order: 1 },
  { name: "done", colour: "#B0E2D3", order: 2 },
];

type TaskDefinitionsSettings = {
  taskDefinitions: TaskDefinition[];
};

const TaskDefinitionsSettings = () => {
  const { settings, setSettings } = useSettingsContext();
  const { t } = useTranslation();

  // Memoize original values
  const originalSettings = useMemo(() => {
    const taskDefinitions = (settings as any)?.taskDefinitions;
    return {
      taskDefinitions: taskDefinitions ?? [],
    } as TaskDefinitionsSettings;
  }, [settings]);

  // Local state
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinition[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Initialize local state when settings change
  useEffect(() => {
    // Migrate existing task definitions to include order property
    const migratedTaskDefinitions = originalSettings.taskDefinitions.map(task => ({
      ...task,
      states: task.states.map((state, index) => ({
        ...state,
        order: state.order !== undefined ? state.order : index,
      })),
    }));

    setTaskDefinitions(migratedTaskDefinitions);
    setIsInitialized(true);
  }, [originalSettings]);

  // Check for validation errors (state names with spaces)
  const hasValidationErrors = useMemo(() => {
    return taskDefinitions.some(taskDef =>
      taskDef.states.some(state => state.name.includes(' '))
    );
  }, [taskDefinitions]);

  // Calculate if data is dirty
  const isDirty = useMemo(() => {
    if (!isInitialized) {
      return false;
    }

    const currentSettings = { taskDefinitions };
    const original = {
      taskDefinitions: originalSettings.taskDefinitions,
    };

    return JSON.stringify(currentSettings) !== JSON.stringify(original);
  }, [taskDefinitions, originalSettings, isInitialized]);

  const handleSave = async () => {
    if (hasValidationErrors) {
      return; // Don't save if there are validation errors
    }
    setIsSaving(true);
    try {
      await setSettings({
        taskDefinitions,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addTaskDefinition = () => {
    const defaultStates: TaskState[] = DEFAULT_STATES.map((state, index) => ({
      ...state,
      id: uuid(),
      order: index,
    }));

    const newTaskDefinition: TaskDefinition = {
      id: uuid(),
      name: "",
      icon: "PersonIcon",
      states: defaultStates,
    };
    setTaskDefinitions([...taskDefinitions, newTaskDefinition]);
  };

  const updateTaskDefinition = (id: string, updates: Partial<TaskDefinition>) => {
    setTaskDefinitions(
      taskDefinitions.map((task) =>
        task.id === id ? { ...task, ...updates } : task
      )
    );
  };

  const deleteTaskDefinition = (id: string) => {
    setTaskDefinitions(taskDefinitions.filter((task) => task.id !== id));
  };

  const addState = (taskId: string) => {
    const task = taskDefinitions.find((t) => t.id === taskId);
    if (task) {
      const nextOrder = Math.max(...task.states.map(s => s.order), -1) + 1;
      const newState: TaskState = {
        id: uuid(),
        name: "",
        colour: "#000000",
        order: nextOrder,
      };
      updateTaskDefinition(taskId, {
        states: [...task.states, newState],
      });
    }
  };

  const updateState = (taskId: string, stateId: string, updates: Partial<TaskState>) => {
    const task = taskDefinitions.find((t) => t.id === taskId);
    if (task) {
      const updatedStates = task.states.map((state) =>
        state.id === stateId ? { ...state, ...updates } : state
      );
      updateTaskDefinition(taskId, { states: updatedStates });
    }
  };

  const deleteState = (taskId: string, stateId: string) => {
    const task = taskDefinitions.find((t) => t.id === taskId);
    if (task) {
      const updatedStates = task.states.filter((state) => state.id !== stateId);
      updateTaskDefinition(taskId, { states: updatedStates });
    }
  };

  const moveStateUp = (taskId: string, stateId: string) => {
    const task = taskDefinitions.find((t) => t.id === taskId);
    if (task) {
      const sortedStates = [...task.states].sort((a, b) => a.order - b.order);
      const stateIndex = sortedStates.findIndex(s => s.id === stateId);

      if (stateIndex > 0) {
        const updatedStates = sortedStates.map((state, index) => {
          if (index === stateIndex) {
            return { ...state, order: sortedStates[stateIndex - 1].order };
          } else if (index === stateIndex - 1) {
            return { ...state, order: sortedStates[stateIndex].order };
          }
          return state;
        });
        updateTaskDefinition(taskId, { states: updatedStates });
      }
    }
  };

  const moveStateDown = (taskId: string, stateId: string) => {
    const task = taskDefinitions.find((t) => t.id === taskId);
    if (task) {
      const sortedStates = [...task.states].sort((a, b) => a.order - b.order);
      const stateIndex = sortedStates.findIndex(s => s.id === stateId);

      if (stateIndex < sortedStates.length - 1) {
        const updatedStates = sortedStates.map((state, index) => {
          if (index === stateIndex) {
            return { ...state, order: sortedStates[stateIndex + 1].order };
          } else if (index === stateIndex + 1) {
            return { ...state, order: sortedStates[stateIndex].order };
          }
          return state;
        });
        updateTaskDefinition(taskId, { states: updatedStates });
      }
    }
  };

  // Get available icons
  const availableIcons = Object.keys(Icons).map((iconName) => ({
    name: iconName,
    component: (Icons as any)[iconName],
  }));

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          {/* Header with dirty indicator */}
          <SettingsPageHeader title="Task Definitions Settings" isDirty={isDirty} />

          {/* Validation Error Message */}
          {hasValidationErrors && (
            <Alert severity="error">
              One or more state names contain spaces. State names must not contain spaces. Use underscores instead (e.g., &quot;not_requested&quot; instead of &quot;not requested&quot;).
            </Alert>
          )}

          {/* Add Task Definition Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addTaskDefinition}
            >
              Add Task Definition
            </Button>
          </Box>

          {/* Task Definitions List */}
          {taskDefinitions.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              No task definitions configured. Click &quot;Add Task Definition&quot; to get started.
            </Typography>
          ) : (
            taskDefinitions.map((taskDefinition) => (
              <Card key={taskDefinition.id} variant="outlined">
                <CardContent>
                  <Stack spacing={3}>
                    {/* Task Definition Header */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <I18nField
                        label="Task Name"
                        value={taskDefinition.name}
                        onChange={(value) =>
                          updateTaskDefinition(taskDefinition.id, {
                            name: value,
                          })
                        }
                        placeholder="Enter task name (e.g., 'bloodTest')"
                        namespace="tasks"
                        sx={{ flex: 1 }}
                      />
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Icon</InputLabel>
                        <Select
                          value={taskDefinition.icon}
                          onChange={(e) =>
                            updateTaskDefinition(taskDefinition.id, {
                              icon: e.target.value,
                            })
                          }
                          label="Icon"
                        >
                          {availableIcons.map((icon) => (
                            <MenuItem key={icon.name} value={icon.name}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <icon.component style={{ width: 20, height: 20 }} />
                                <Typography>{icon.name}</Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        onClick={() => deleteTaskDefinition(taskDefinition.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>

                    {/* States Section */}
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">States</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addState(taskDefinition.id)}
                        >
                          Add State
                        </Button>
                      </Stack>

                      {taskDefinition.states.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No states configured. Click &quot;Add State&quot; to get started.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {taskDefinition.states
                            .sort((a, b) => a.order - b.order)
                            .map((state, index) => (
                              <Card key={state.id} variant="outlined">
                                <CardContent>
                                  <Stack spacing={2}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <StateNameField
                                          value={state.name}
                                          onChange={(newValue) =>
                                            updateState(taskDefinition.id, state.id, {
                                              name: newValue,
                                            })
                                          }
                                          error={state.name.includes(' ')}
                                        />
                                      </Box>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <input
                                          type="color"
                                          value={state.colour}
                                          onChange={(e) =>
                                            updateState(taskDefinition.id, state.id, {
                                              colour: e.target.value,
                                            })
                                          }
                                          style={{
                                            width: 40,
                                            height: 40,
                                            border: "none",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                          }}
                                        />
                                        <TextField
                                          label="Colour"
                                          value={state.colour}
                                          onChange={(e) =>
                                            updateState(taskDefinition.id, state.id, {
                                              colour: e.target.value,
                                            })
                                          }
                                          size="small"
                                          sx={{ width: 120 }}
                                        />
                                      </Stack>
                                      <Stack direction="row" spacing={0.5}>
                                        <IconButton
                                          onClick={() =>
                                            moveStateUp(taskDefinition.id, state.id)
                                          }
                                          size="small"
                                          disabled={index === 0}
                                        >
                                          <KeyboardArrowUpIcon />
                                        </IconButton>
                                        <IconButton
                                          onClick={() =>
                                            moveStateDown(taskDefinition.id, state.id)
                                          }
                                          size="small"
                                          disabled={index === taskDefinition.states.length - 1}
                                        >
                                          <KeyboardArrowDownIcon />
                                        </IconButton>
                                        <IconButton
                                          onClick={() =>
                                            deleteState(taskDefinition.id, state.id)
                                          }
                                          size="small"
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Stack>
                                    </Stack>
                                    <Tooltip title={state.name ? t(`tasks.${state.name}`) : "Unnamed State"} arrow>
                                      <ButtonBase
                                        css={styles.taskItem}
                                        sx={{
                                          backgroundColor: state.colour,
                                          color: "black",
                                          fontWeight: "bold",
                                          minWidth: "auto",
                                          padding: "0.5rem",
                                          borderRadius: "0.5rem",
                                          alignSelf: "flex-start",
                                        }}
                                      >
                                        {(Icons as any)[taskDefinition.icon] &&
                                          React.createElement((Icons as any)[taskDefinition.icon], {
                                            style: { color: "black", width: 20, height: 20 }
                                          })
                                        }
                                      </ButtonBase>
                                    </Tooltip>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        {/* Floating Save Button */}
        <FloatingSaveButton show={isDirty && !hasValidationErrors} onSave={handleSave} isSaving={isSaving} />
      </Box>
    </Box>
  );
};

export { TaskDefinitionsSettings };
